import { Box, Flex, StyleProps } from '@chakra-ui/react';
import { format, isValid, parse } from 'date-fns';
import { ChangeEvent, FC, KeyboardEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { rem } from '@bonusly/ds-utils';

import { assertIsDateRange, DateRange, DateValue } from '../../types';
import { useDatePickerContext } from '../context';

type CalendarHeaderProps = StyleProps;

type FieldKey = 'start' | 'end';

// Derive locale-aware date templates from the user's i18n language so a German
// user typing "5.4.2026" parses as April 5 (not May 4) and an en-US user typing
// "5/1/2025" parses as May 1. We build the templates from
// Intl.DateTimeFormat#formatToParts using a sample date with distinct
// month/day/year values, then plug in date-fns format tokens.
const SAMPLE_YEAR = 2099;
const SAMPLE_MONTH_INDEX = 0;
const SAMPLE_DAY = 2;

const buildLocaleDateTemplates = (locale: string) => {
	const sample = new Date(SAMPLE_YEAR, SAMPLE_MONTH_INDEX, SAMPLE_DAY);
	// Pin to Gregorian + latin digits so calendars with alternate eras (e.g.
	// ja-JP-u-ca-japanese, th-TH Buddhist) still produce a template that
	// round-trips through date-fns parse/format.
	const intlOptions: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		calendar: 'gregory',
		numberingSystem: 'latn',
	};
	let parts: Intl.DateTimeFormatPart[];

	try {
		parts = new Intl.DateTimeFormat(locale, intlOptions).formatToParts(sample);
	} catch {
		parts = new Intl.DateTimeFormat('en-US', intlOptions).formatToParts(sample);
	}

	const renderTemplate = (yearTok: string, monthTok: string, dayTok: string) =>
		parts
			.map((part) => {
				switch (part.type) {
					case 'year':
						return yearTok;
					case 'month':
						return monthTok;
					case 'day':
						return dayTok;
					default:
						return part.value;
				}
			})
			.join('');

	const display = renderTemplate('yyyy', 'M', 'd');
	const parseFormats = [
		renderTemplate('yyyy', 'M', 'd'),
		renderTemplate('yyyy', 'MM', 'dd'),
		renderTemplate('yy', 'M', 'd'),
		renderTemplate('yy', 'MM', 'dd'),
	];

	return { display, parseFormats };
};

const ISO_PARSE_FORMAT = 'yyyy-MM-dd';

const parseInputDate = (input: string, parseFormats: string[]): Date | null => {
	const trimmed = input.trim();
	if (!trimmed) return null;

	for (const fmt of parseFormats) {
		const parsed = parse(trimmed, fmt, new Date());
		if (isValid(parsed)) return parsed;
	}

	const iso = parse(trimmed, ISO_PARSE_FORMAT, new Date());
	if (isValid(iso)) return iso;

	return null;
};

const formatInputDate = (date: DateValue, displayFormat: string): string =>
	date && isValid(date) ? format(date, displayFormat) : '';

export const CalendarRangeHeader: FC<CalendarHeaderProps> = (props) => {
	const { i18n, t } = useTranslation('components');
	const { interimValue, setRange } = useDatePickerContext();

	assertIsDateRange(interimValue);

	const [startDate, endDate] = interimValue;

	const { display: displayFormat, parseFormats } = useMemo(
		() => buildLocaleDateTemplates(i18n.language || 'en-US'),
		[i18n.language]
	);

	const [startInput, setStartInput] = useState(() => formatInputDate(startDate, displayFormat));
	const [endInput, setEndInput] = useState(() => formatInputDate(endDate, displayFormat));
	const focusedField = useRef<FieldKey | null>(null);

	// Adjust local input state during render when the upstream interim range
	// (or display format) changes — preferred over a useEffect-driven sync,
	// per the project's "no derived state in effects" rule.
	const [prevStart, setPrevStart] = useState<DateValue>(startDate);
	const [prevEnd, setPrevEnd] = useState<DateValue>(endDate);
	const [prevDisplayFormat, setPrevDisplayFormat] = useState(displayFormat);

	if (displayFormat !== prevDisplayFormat) {
		setPrevDisplayFormat(displayFormat);
		setPrevStart(startDate);
		setPrevEnd(endDate);
		setStartInput(formatInputDate(startDate, displayFormat));
		setEndInput(formatInputDate(endDate, displayFormat));
	} else {
		if (startDate !== prevStart) {
			setPrevStart(startDate);
			if (focusedField.current !== 'start') {
				setStartInput(formatInputDate(startDate, displayFormat));
			}
		}
		if (endDate !== prevEnd) {
			setPrevEnd(endDate);
			if (focusedField.current !== 'end') {
				setEndInput(formatInputDate(endDate, displayFormat));
			}
		}
	}

	const commitRange = (newStart: DateValue, newEnd: DateValue) => {
		if (!setRange) return;
		// Mirror useDateRangeControl: ranges always read low → high. Flipping
		// rather than rejecting matches what happens when users click dates
		// in reverse on the calendar.
		let ordered: DateRange = [newStart, newEnd];
		if (newStart && newEnd && newStart > newEnd) {
			ordered = [newEnd, newStart];
		}
		setRange(ordered);
	};

	const handleCommit = (field: FieldKey) => {
		if (field === 'start') {
			const parsed = parseInputDate(startInput, parseFormats);
			if (parsed) {
				commitRange(parsed, endDate);
			} else {
				setStartInput(formatInputDate(startDate, displayFormat));
			}
			return;
		}

		const parsed = parseInputDate(endInput, parseFormats);
		if (parsed) {
			commitRange(startDate, parsed);
		} else {
			setEndInput(formatInputDate(endDate, displayFormat));
		}
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			event.currentTarget.blur();
		}
	};

	// Native <input> elements ship with a default `size` attribute of 20,
	// which sets their intrinsic preferred width to ~20em. That ignores
	// `flex-basis: 0` and inflates the parent flex container (and therefore
	// the popup) well past the calendar's 280px. Explicitly setting size=1
	// collapses the intrinsic width so flex-grow can size each field to
	// share the row equally — matching the original read-only Box behavior.
	const sharedInputProps = {
		as: 'input',
		size: 1,
		bg: 'bg.surface',
		borderColor: 'border.default',
		borderRadius: 'md',
		borderStyle: 'solid',
		borderWidth: '1px',
		flexBasis: 0,
		flexGrow: 1,
		fontWeight: 'normal',
		minH: rem(40),
		minW: 0,
		outline: 'none',
		px: 3,
		py: 2,
		_focusVisible: { borderColor: 'border.focus' },
		_placeholder: { color: 'text.softest' },
	} as const;

	const placeholder = t('date_picker.date_placeholder');

	return (
		<Flex alignItems="center" mb={1} columnGap={2} {...props}>
			<Box
				{...sharedInputProps}
				aria-label={t('date_picker.start_date')}
				placeholder={placeholder}
				value={startInput}
				color={startDate ? 'text.default' : 'text.softest'}
				onChange={(event: ChangeEvent<HTMLInputElement>) => setStartInput(event.target.value)}
				onFocus={() => {
					focusedField.current = 'start';
				}}
				onBlur={() => {
					focusedField.current = null;
					handleCommit('start');
				}}
				onKeyDown={handleKeyDown}
			/>
			<Box
				{...sharedInputProps}
				aria-label={t('date_picker.end_date')}
				placeholder={placeholder}
				value={endInput}
				color={endDate ? 'text.default' : 'text.softest'}
				onChange={(event: ChangeEvent<HTMLInputElement>) => setEndInput(event.target.value)}
				onFocus={() => {
					focusedField.current = 'end';
				}}
				onBlur={() => {
					focusedField.current = null;
					handleCommit('end');
				}}
				onKeyDown={handleKeyDown}
			/>
		</Flex>
	);
};
